import React, { useEffect } from 'react';
import SEO from '../SEO';
import seoData from '../../seoData';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { CheckCircle, ArrowRight, Shield, Pipette, ArrowRightLeft } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import PageHero from '../ui/PageHero';
import { useNavigate } from 'react-router-dom';

const PipelineRepair = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SEO title={seoData["services/pipeline-repair"]?.title} description={seoData["services/pipeline-repair"]?.description} path="/services/pipeline-repair" keywords={seoData["services/pipeline-repair"]?.keywords || []} />

      <PageHero
        badge="Core Services"
        title="Pipeline Coating & Pipe Rehabilitation"
        description="Pipeline coating contractor and pipe rehabilitation specialist in California. Industrial pipe repair, pipe lining, composite wraps, and leak sealing. ISO 24817 / ASME PCC-2 compliant. No hot work, no shutdown — repair live pipes under pressure."
        backgroundKey="pipeline-repair"
        buttonText="Get In Touch"
      />

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Problems We Solve</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {['Active pipe leaks requiring emergency sealing under pressure',
              'External corrosion under insulation (CUI) on process piping',
              'Internal corrosion and wall thinning in carbon steel pipes',
              'Pipe joint and flange leaks from gasket failure and corrosion',
              'FRP and fiberglass pipe delamination and cracking',
              'Concrete pressure pipe corrosion and joint deterioration'].map((challenge, index) => (
              <div key={index} className="flex items-start p-4 bg-teal-50 rounded-lg">
                <Pipette className="text-teal-600 mr-3 mt-0.5 flex-shrink-0" size={20} />
                <span className="text-gray-700 text-sm">{challenge}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Our Solutions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Card className="border-l-4 border-l-teal-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <ArrowRightLeft className="text-teal-600 mr-3 flex-shrink-0" size={22} />
                  Composite Pipe Wraps
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">ISO 24817 and ASME PCC-2 compliant composite repair systems for structural reinforcement of corroded pipe. Carbon fiber and fiberglass wraps restore pipe wall thickness and pressure rating without replacement or shutdown.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-teal-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <ArrowRightLeft className="text-teal-600 mr-3 flex-shrink-0" size={22} />
                  Live Leak Sealing
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Emergency leak repair on pressurized pipes using epoxy-based sealing systems. Stop active leaks on process pipes, cooling water, fire water, and utility piping without depressurization or hot work.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-teal-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <ArrowRightLeft className="text-teal-600 mr-3 flex-shrink-0" size={22} />
                  Internal Pipe Lining
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Epoxy pipe lining systems for internal corrosion protection of steel, cast iron, and concrete pipe. Restore flow capacity and prevent further wall loss on aging piping systems.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-teal-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <ArrowRightLeft className="text-teal-600 mr-3 flex-shrink-0" size={22} />
                  External Pipe Coating
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Corrosion protection for exposed and buried piping. Surface-tolerant epoxy systems for CUI repair, and long-term atmospheric corrosion prevention for pipe racks and utility piping.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Why Choose BCS</h2>
              <div className="space-y-3">
                {['No hot work — eliminate fire permits, ignition risk, and safety shutdowns',
                  'Repair under pressure — many systems applied on live, pressurized pipes',
                  'ISO 24817 / ASME PCC-2 compliant engineering and application',
                  '50-70% cost savings vs. pipe replacement',
                  'No production shutdown for most repair applications',
                  'Extend pipe service life 15-25 years',
                  'Emergency response for active leaks — 24/7 availability',
                  'Full engineering documentation: repair calculations, QA/QC records'].map((benefit, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle className="text-green-700 mr-3 mt-0.5 flex-shrink-0" size={18} />
                    <span className="text-gray-700 text-sm">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-blue-900 text-white p-8 rounded-lg">
              <Shield className="mb-4" size={48} />
              <h3 className="text-xl font-bold mb-4">Belzona Products We Use</h3>
              <div className="space-y-3">
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="font-semibold text-sm text-slate-900">Belzona 1111 (Super Metal)</p>
                  <p className="text-xs text-slate-500 mt-1">Pipe surface rebuild and pinhole repair</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="font-semibold text-sm text-slate-900">Belzona 1212 (Fluidmetal)</p>
                  <p className="text-xs text-slate-500 mt-1">Large area pipe wall rebuild</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="font-semibold text-sm text-slate-900">Belzona 5831 (ST-Barrier)</p>
                  <p className="text-xs text-slate-500 mt-1">Surface-tolerant external pipe coating</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="font-semibold text-sm text-slate-900">Belzona SuperWrap II</p>
                  <p className="text-xs text-slate-500 mt-1">ISO 24817 structural composite pipe repair</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Pipe Leak? We Fix It Without Shutdown.</h2>
          <p className="text-blue-100 mb-8">Contact our pipeline repair specialists for emergency leak sealing or planned pipe rehabilitation. 24/7 emergency response available.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-white text-blue-900 hover:bg-gray-100" onClick={() => window.location.href = 'tel:+19493719948'}>
              Call (949) 371-9948
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-blue-800" onClick={() => navigate('/#contact')}>
              Request Assessment <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PipelineRepair;
